import {
  isValidElement,
  cloneElement,
  type ReactNode,
  type ReactElement,
} from "react";
import {
  ListPagination,
  SearchStatusFilterBar,
  type ListPaginationProps,
  type SearchStatusFilterBarProps,
} from "@/shared/list/useListChrome";
import { useMediaQuery } from "@/shared/lib/useMediaQuery";
import { ErrorState } from "@/shared/ui/core/ErrorState";
import { Skeleton } from "@/shared/ui/core/Skeleton";

export type ListPageTemplateProps = {
  /** Optional ModuleInsights / KPI strip (show/hide via `usePageInsightsPanel`). */
  insights?: ReactNode;
  /** Filter panel (SearchStatusFilterBar or custom). */
  filter?: ReactNode;
  filtersOpen?: boolean;
  filterActiveCount?: number;
  /** Main table / content. */
  children: ReactNode;
  /** Desktop page controls — hidden on mobile (card + load-more). */
  pagination?: ListPaginationProps;
  /** Full list initial loading (renders Skeleton instead of unmounting the UI). */
  loading?: boolean;
  /** Page transition loading (forwards to table & pagination controls). */
  pageLoading?: boolean;
  errorTitle?: string;
  errorDescription?: string;
  onRetry?: () => void;
  /** Optional banner / notice above filters. */
  notice?: ReactNode;
};

/**
 * Standard Ops list layout — notice → insights → filter → table/cards → pagination (desktop).
 * The UI layout shell stays mounted and stable: table rows transition in-place with
 * skeleton on initial load and overlay during filter/page transitions.
 */
export function ListPageTemplate({
  insights,
  filter,
  children,
  pagination,
  loading,
  pageLoading,
  errorTitle,
  errorDescription,
  onRetry,
  notice,
}: ListPageTemplateProps) {
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  // Propagate loading & pageLoading to child element (e.g. DataTable) if not already set,
  // preventing layout destruction and enabling in-place skeleton / loading overlay.
  let renderedChildren = children;
  if (
    isValidElement(children) &&
    typeof children.type !== "string" &&
    ((loading !== undefined && (children.props as any).loading === undefined) ||
      (pageLoading !== undefined && (children.props as any).pageLoading === undefined))
  ) {
    renderedChildren = cloneElement(children as ReactElement<any>, {
      loading: (children.props as any).loading ?? loading,
      pageLoading: (children.props as any).pageLoading ?? pageLoading,
    });
  }

  return (
    <div className="space-y-3">
      {notice}
      {insights}
      {filter}
      {errorTitle ? (
        <ErrorState
          title={errorTitle}
          description={errorDescription}
          onRetry={onRetry}
        />
      ) : loading && !children ? (
        <div className="rounded-control border border-border bg-surface p-4 shadow-xs" aria-busy>
          <Skeleton variant="table" rows={6} />
        </div>
      ) : (
        renderedChildren
      )}
      {pagination &&
      isDesktop &&
      !errorTitle &&
      pagination.total > (pagination.minItems !== undefined ? pagination.minItems - 1 : 10) ? (
        <ListPagination
          {...pagination}
          loading={pagination.loading ?? pageLoading}
        />
      ) : null}
    </div>
  );
}


export { SearchStatusFilterBar, ListPagination };
export type { SearchStatusFilterBarProps, ListPaginationProps };
